import React, { useState, useEffect } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Label, UncontrolledDropdown, 
    DropdownToggle, DropdownMenu, DropdownItem, Spinner, Modal, ModalHeader, ModalBody, UncontrolledCarousel,
    ModalFooter
} from 'reactstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { APIClient } from '../../helpers/api_helper';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import SweetAlert from 'react-bootstrap-sweetalert';
import UserDetailsModal from '../../Components/Common/UserDetailsModal';
import useUserDetails from '../../hooks/useUserDetails';

const ModerateMedia = () => {
    document.title = "Moderate Media";
    
    const [mediaData, setMediaData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 10,
        totalCount: 0
    });
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [viewModal, setViewModal] = useState(false);
    const [confirmModal, setConfirmModal] = useState(false);
    const [moderationAction, setModerationAction] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [activeTab, setActiveTab] = useState('PENDING');

    const {
        userDetailsModal,
        setUserDetailsModal,
        selectedUser,
        loadingUser,
        fetchUserDetails
    } = useUserDetails();

    const fetchMediaData = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            const response = await APIClient.get(`/mediaForContentModeration?status=${activeTab}&limit=${pagination.limit}&offset=${offset}`);
            
            if (response.data && response.data.data) {
                setMediaData(response.data.data.moderationData);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: response.data.data.totalCount
                }));
            }
        } catch (error) {
            console.error('Error fetching media:', error);
            toast.error("Failed to fetch media data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMediaData(1);
    }, [activeTab]);

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .carousel {
                background-color: #f8f9fa;
            }
            
            .carousel-inner {
                height: 250px;
            }
            
            .carousel-item {
                height: 250px;
                text-align: center;
                background-color: #f8f9fa;
            }
            
            .carousel-item img {
                height: 100%;
                max-width: 100%;
                object-fit: contain;
                margin: auto;
            }

        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const handleModeration = async (status) => {
        try {
            setLoading(true);
            await APIClient.patch('/contentModerationResult', {
                moderationId: selectedMedia.moderationId,
                status: status
            });
            
            toast.success(`Content ${status === 'ACCEPTED' ? 'approved' : 'rejected'} successfully`);
            setConfirmModal(false);
            setViewModal(false);
            fetchMediaData(pagination.currentPage);
        } catch (error) {
            console.error('Error moderating content:', error);
            toast.error("Failed to moderate content");
        } finally {
            setLoading(false);
        }
    };

    const openSweetAlert = (action) => {
        setModerationAction(action);
        setShowAlert(true);
    };

    const getImageUrl = (url) => {
        if (!url) return '/images/default-avatar.png';
        if (url.startsWith('http')) return url;
        
        const baseUrl = process.env.REACT_APP_MEDIA_URL.replace(/\/$/, '');
        const imagePath = url.replace(/^\//, '');
        return `${baseUrl}/${imagePath}`;
    };

    // Add pagination rendering function
    const renderPagination = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        
        // Function to get page numbers to show
        const getPageNumbers = () => {
            const delta = 2; // Number of pages to show before and after current page
            const range = [];
            const rangeWithDots = [];
            let l;

            for (let i = 1; i <= totalPages; i++) {
                if (i === 1 || i === totalPages || 
                    (i >= pagination.currentPage - delta && i <= pagination.currentPage + delta)) {
                    range.push(i);
                }
            }

            for (let i of range) {
                if (l) {
                    if (i - l === 2) {
                        rangeWithDots.push(l + 1);
                    } else if (i - l !== 1) {
                        rangeWithDots.push('...');
                    }
                }
                rangeWithDots.push(i);
                l = i;
            }

            return rangeWithDots;
        };

        return (
            <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <Col sm={4}>
                    <div className="text-muted">Showing <span className="fw-semibold">{mediaData.length}</span> of <span className="fw-semibold">{pagination.totalCount}</span> Results</div>
                </Col>
                <Col sm={8}>
                    <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                        <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => fetchMediaData(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                            >
                                <i className="ri-arrow-left-s-line"></i>
                            </Button>
                        </li>
                        
                        {getPageNumbers().map((pageNumber, index) => (
                            <li 
                                key={index} 
                                className={`page-item ${pageNumber === pagination.currentPage ? 'active' : ''} ${pageNumber === '...' ? 'disabled' : ''}`}
                            >
                                <Button
                                    className="page-link"
                                    onClick={() => pageNumber !== '...' && fetchMediaData(pageNumber)}
                                    disabled={pageNumber === '...'}
                                >
                                    {pageNumber}
                                </Button>
                            </li>
                        ))}

                        <li className={`page-item ${pagination.currentPage === totalPages ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => fetchMediaData(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === totalPages}
                            >
                                <i className="ri-arrow-right-s-line"></i>
                            </Button>
                        </li>
                    </ul>
                </Col>
            </Row>
        );
    };

    return (
        <div className="page-content">
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
            <Container fluid>
                <BreadCrumb title="Moderate Media" pageTitle="Content" />
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader>
                                <ul className="nav nav-tabs-custom card-header-tabs border-bottom-0">
                                    <li className="nav-item">
                                        <a
                                            className={`nav-link ${activeTab === 'PENDING' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('PENDING')}
                                            role="button"
                                        >
                                            <i className="fas fa-hourglass-half me-1"></i>
                                            Pending Review
                                        </a>
                                    </li>
                                    <li className="nav-item">
                                        <a
                                            className={`nav-link ${activeTab === 'ACCEPTED' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('ACCEPTED')}
                                            role="button"
                                        >
                                            <i className="fas fa-check-circle me-1"></i>
                                            Accepted
                                        </a>
                                    </li>
                                    <li className="nav-item">
                                        <a
                                            className={`nav-link ${activeTab === 'REJECTED' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('REJECTED')}
                                            role="button"
                                        >
                                            <i className="fas fa-times-circle me-1"></i>
                                            Rejected
                                        </a>
                                    </li>
                                </ul>
                            </CardHeader>
                            <CardBody>
                                <div className="table-responsive">
                                    <Table className="table-striped table-nowrap align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>SL No</th>
                                                <th>Date</th>
                                                <th>User</th>
                                                <th>Media Type</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {mediaData.map((item, index) => (
                                                <tr key={item._id}>
                                                    <td>{index + 1}</td>
                                                    <td>{moment(item.createdTs).format('DD MMM YYYY, hh:mm A')}</td>
                                                    <td>
                                                        <div 
                                                            className="text-body fw-medium cursor-pointer"
                                                            onClick={() => fetchUserDetails(item.userDetails._id)}
                                                        >
                                                            {item.userDetails.username}
                                                        </div>
                                                    </td>
                                                    <td>{item.mediaType}</td>
                                                    <td>
                                                        <span className="badge bg-warning">
                                                            {item.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => {
                                                                setSelectedMedia(item);
                                                                setViewModal(true);
                                                            }}
                                                        >
                                                            View Post
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                {renderPagination()}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* View Media Modal */}
            <Modal
                isOpen={viewModal}
                toggle={() => setViewModal(!viewModal)}
                size="lg"
                centered
            >
                <ModalHeader toggle={() => setViewModal(!viewModal)}>
                    View Content
                </ModalHeader>
                <ModalBody>
                    {selectedMedia && (
                        <>
                            {selectedMedia.mediaType === 'VIDEO' ? (
                                <div className="ratio ratio-16x9 mb-3">
                                    <iframe 
                                        src={selectedMedia.postVideoUrl}
                                        title="Content Preview"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            ) : (
                                <div className="mb-4">
                                    <UncontrolledCarousel
                                        interval={3000}
                                        items={[{
                                            altText: 'Content Image',
                                            caption: ' ',
                                            key: 1,
                                            src: getImageUrl(selectedMedia.mediaUrl),
                                            style: {
                                                height: '300px',
                                                objectFit: 'contain',
                                                backgroundColor: '#f8f9fa'
                                            }
                                        }]}
                                        indicators={true}
                                        controls={true}
                                    />
                                </div>
                            )}
                            <div className="d-flex justify-content-center gap-2">
                                {(activeTab === 'PENDING' || activeTab === 'REJECTED') && (
                                    <Button
                                        color="success"
                                        onClick={() => openSweetAlert('ACCEPTED')}
                                    >
                                        Approve
                                    </Button>
                                )}
                                {(activeTab === 'PENDING' || activeTab === 'ACCEPTED') && (
                                    <Button
                                        color="danger"
                                        onClick={() => openSweetAlert('REJECTED')}
                                    >
                                        Reject
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </ModalBody>
            </Modal>

            {showAlert && (
                <SweetAlert
                    warning
                    showCancel
                    confirmBtnText={`Yes, ${moderationAction === 'ACCEPTED' ? 'approve' : 'reject'} it!`}
                    confirmBtnBsStyle={moderationAction === 'ACCEPTED' ? 'success' : 'danger'}
                    cancelBtnBsStyle="light"
                    title="Are you sure?"
                    onConfirm={() => {
                        handleModeration(moderationAction);
                        setShowAlert(false);
                    }}
                    onCancel={() => setShowAlert(false)}
                >
                    Do you really want to {moderationAction === 'ACCEPTED' ? 'approve' : 'reject'} this content?
                </SweetAlert>
            )}

            {loading && (
                <div className="overlay">
                    <Spinner color="primary" />
                </div>
            )}

            <UserDetailsModal 
                isOpen={userDetailsModal}
                toggle={() => setUserDetailsModal(false)}
                user={selectedUser}
                loading={loadingUser}
            />

            <style>
                {`
                    .cursor-pointer {
                        cursor: pointer;
                    }
                    .cursor-pointer:hover {
                        color: #405189 !important;
                        text-decoration: underline;
                    }
                `}
            </style>
        </div>
    );
};

export default ModerateMedia;